export type AssignmentPayInput = {
  pay?: string | number | null
  pay_flat?: string | number | null
  pay_type?: string | null
  pay_type_snapshot?: string | null
  pay_type_label?: string | null
  estimated_duration?: string | number | null
  pay_scale_base_pay?: string | number | null
  day_pay_rate_snapshot?: string | number | null
  peak_pay_rate_snapshot?: string | number | null
  total_pay?: string | number | null
}

export type AssignmentPayoutDisplay =
  | { mode: 'amount'; amount: number }
  | { mode: 'label'; label: string }
  | { mode: 'dash' }

const DEFAULT_PAY_TYPE_LABELS: Record<string, string> = {
  per_hour: 'Hourly',
  per_day: 'Daily',
  per_job: 'Per job',
  per_week: 'Weekly',
  per_month: 'Monthly',
}

const toOptionalNumber = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const firstPositivePayAmount = (...values: Array<string | number | null | undefined>) => {
  for (const value of values) {
    const amount = toOptionalNumber(value)
    if (amount !== null && amount > 0) {
      return amount
    }
  }

  return null
}

export const normalizeAssignmentPayType = (
  assignment: Pick<AssignmentPayInput, 'pay_type' | 'pay_type_snapshot'>,
) => String(assignment.pay_type ?? assignment.pay_type_snapshot ?? 'per_hour').trim().toLowerCase()

export const isCalculatedAssignmentPayType = (payType?: string | null) => {
  const normalized = String(payType ?? '').trim().toLowerCase()
  return normalized === 'per_hour' || normalized === 'per_job'
}

export const isDisplayOnlyAssignmentPayType = (payType?: string | null) =>
  String(payType ?? '').trim().toLowerCase() === 'per_day'

export const showsAssignmentPayTypeLabelOnly = (payType?: string | null) =>
  !isCalculatedAssignmentPayType(payType) && !isDisplayOnlyAssignmentPayType(payType)

export const isWorkerInvoicePayType = isCalculatedAssignmentPayType

export const resolveAssignmentPayTypeLabel = (
  assignment: Pick<AssignmentPayInput, 'pay_type' | 'pay_type_snapshot' | 'pay_type_label'>,
) => {
  const label = String(assignment.pay_type_label ?? '').trim()
  if (label) {
    return label
  }

  const payType = normalizeAssignmentPayType(assignment)
  if (DEFAULT_PAY_TYPE_LABELS[payType]) {
    return DEFAULT_PAY_TYPE_LABELS[payType]
  }

  return payType
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export const resolveAssignmentBasePay = (assignment: AssignmentPayInput) => {
  const basePay = toOptionalNumber(assignment.pay)
  if (basePay !== null && basePay > 0) {
    return basePay
  }

  const flatPay = toOptionalNumber(assignment.pay_flat)
  if (flatPay !== null && flatPay > 0) {
    return flatPay
  }

  return basePay ?? flatPay ?? 0
}

export const resolveAssignmentDisplayRate = (assignment: AssignmentPayInput) => {
  const payType = normalizeAssignmentPayType(assignment)

  if (payType === 'per_hour') {
    return firstPositivePayAmount(assignment.pay_scale_base_pay, assignment.pay)
  }

  if (payType === 'per_day') {
    return firstPositivePayAmount(
      assignment.pay_scale_base_pay,
      assignment.pay,
      assignment.pay_flat,
      assignment.day_pay_rate_snapshot,
    )
  }

  if (payType === 'per_job') {
    return firstPositivePayAmount(
      assignment.pay_scale_base_pay,
      assignment.pay,
      assignment.pay_flat,
      assignment.peak_pay_rate_snapshot,
    )
  }

  return null
}

export const calculateEstimatedAssignmentPayout = (assignment: AssignmentPayInput) => {
  const payType = normalizeAssignmentPayType(assignment)
  const basePay = Math.max(resolveAssignmentBasePay(assignment), 0)

  if (payType === 'per_job') {
    return basePay
  }

  if (payType === 'per_hour') {
    const duration = toOptionalNumber(assignment.estimated_duration)
    if (duration === null || duration <= 0) {
      return 0
    }
    return basePay * duration
  }

  return 0
}

export const getEstimatedAssignmentPayoutDisplay = (assignment: AssignmentPayInput): AssignmentPayoutDisplay => {
  const payType = normalizeAssignmentPayType(assignment)

  if (isCalculatedAssignmentPayType(payType)) {
    return { mode: 'amount', amount: calculateEstimatedAssignmentPayout(assignment) }
  }

  if (isDisplayOnlyAssignmentPayType(payType)) {
    const amount = resolveAssignmentDisplayRate(assignment)
    return amount === null ? { mode: 'dash' } : { mode: 'amount', amount }
  }

  const label = resolveAssignmentPayTypeLabel(assignment)
  return label ? { mode: 'label', label } : { mode: 'dash' }
}

export const getActualAssignmentPayoutDisplay = (assignment: AssignmentPayInput): AssignmentPayoutDisplay => {
  const payType = normalizeAssignmentPayType(assignment)

  if (isCalculatedAssignmentPayType(payType)) {
    const totalPay = Number(assignment.total_pay ?? 0)
    return { mode: 'amount', amount: Number.isFinite(totalPay) ? totalPay : 0 }
  }

  if (isDisplayOnlyAssignmentPayType(payType)) {
    const amount = resolveAssignmentDisplayRate(assignment)
    return amount === null ? { mode: 'dash' } : { mode: 'amount', amount }
  }

  const label = resolveAssignmentPayTypeLabel(assignment)
  return label ? { mode: 'label', label } : { mode: 'dash' }
}

export const formatAssignmentPayoutDisplay = (
  display: AssignmentPayoutDisplay,
  formatAmount: (amount: number) => string,
) => {
  if (display.mode === 'label') {
    return display.label
  }

  if (display.mode === 'dash') {
    return '-'
  }

  return formatAmount(display.amount)
}

export const sumEstimatedAssignmentPayouts = (assignments: AssignmentPayInput[]) =>
  assignments.reduce((sum, assignment) => {
    if (!isCalculatedAssignmentPayType(normalizeAssignmentPayType(assignment))) {
      return sum
    }
    return sum + calculateEstimatedAssignmentPayout(assignment)
  }, 0)

export const sumActualAssignmentPayouts = (assignments: AssignmentPayInput[]) =>
  assignments.reduce((sum, assignment) => {
    if (!isCalculatedAssignmentPayType(normalizeAssignmentPayType(assignment))) {
      return sum
    }

    const totalPay = Number(assignment.total_pay ?? 0)
    return sum + (Number.isFinite(totalPay) ? totalPay : 0)
  }, 0)
